import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { PHLEBO_PG_INCENTIVE_SLABS, PHLEBO_WALLET_INCENTIVE_SLABS } from "../../constants/phleboIncentiveSlabs";
import { USER, URL } from "../../constants/constant";
import "./MyProgressHub.css";


const getCurrentTier = (amount, slabs) => {
	for (let i = slabs.length - 1; i >= 0; i--) {
		if (amount >= slabs[i].revenueMin) {
			return slabs[i];
		}
	}
	return slabs[0];
};

const getCurrentTierIndex = (amount, slabs) => {
	const currentTier = getCurrentTier(amount, slabs);
	return slabs.findIndex(s => s.category === currentTier.category);
};

const getNextTier = (amount, slabs) => {
	const currentIndex = getCurrentTierIndex(amount, slabs);
	if (currentIndex === slabs.length - 1) {
		return null;
	}
	return slabs[currentIndex + 1];
};

const isMaxTier = (amount, slabs) => {
	return getCurrentTierIndex(amount, slabs) === slabs.length - 1;
};

const getIncentiveEarned = (amount, slabs) => {
	const currentTier = getCurrentTier(amount, slabs);
	return (amount * currentTier.incentivePercent) / 100;
};

const getAmountToNextTier = (amount, slabs) => {
	const nextTier = getNextTier(amount, slabs);
	if (!nextTier) return 0;
	const amountNeeded = nextTier.revenueMin - amount;
	return Math.max(amountNeeded, 0);
};

const TierProgression = ({ title, amount, slabs }) => {
	const currentTier = getCurrentTier(amount, slabs);
	const currentIndex = getCurrentTierIndex(amount, slabs);
	const nextTier = getNextTier(amount, slabs);
	const maxTier = isMaxTier(amount, slabs);
	const amountToNext = getAmountToNextTier(amount, slabs);
	const bonusEarned = getIncentiveEarned(amount, slabs);

	const isPgTier = title.toLowerCase().includes('pg');
	const lastIndex = slabs.length - 1;

	const progressWithinTier = !maxTier
		? Math.min(Math.max((amount - currentTier.revenueMin) / (nextTier.revenueMin - currentTier.revenueMin), 0), 1)
		: 1;
	const overallProgressPercent = ((currentIndex + progressWithinTier) / lastIndex) * 100;

	return (
		<div className={`progress-card ${isPgTier ? 'progress-card-pg' : 'progress-card-wallet'}`} style={{ marginBottom: '20px' }}>
			<div className="card-header">
				<i className={`fa ${isPgTier ? 'fa-credit-card' : 'fa-wallet'}`}></i> {title}
			</div>
			<div className="card-body">
				<div className="tier-journey">
					<div className="tier-journey-summary">
						<div className="tier-current-chip">
							<span className="tier-current-chip-label">{currentTier.category}</span>
							<span className="tier-current-chip-percent">{currentTier.incentivePercent}%</span>
						</div>
						<div className="tier-earned-chip">
							Bonus Earned <strong>₹ {Math.round(bonusEarned).toLocaleString('en-IN')}</strong>
						</div>
					</div>

					<div className="tier-track-wrapper">
						<div className="tier-track-scroll">
							<div className="tier-track-line">
								<div className="tier-track-fill" style={{ width: `${overallProgressPercent}%` }}></div>
							</div>
							<div className="tier-track-nodes">
								{slabs.map((slab, i) => {
									const isCompleted = i < currentIndex || (i === currentIndex && maxTier);
									const isCurrent = i === currentIndex && !maxTier;
									return (
										<div
											key={slab.category}
											className={`tier-node ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
											style={{ left: `${(i / lastIndex) * 100}%` }}
										>
											<div className="tier-node-dot">{isCompleted ? '✓' : i + 1}</div>
											<div className="tier-node-label">{slab.category}</div>
										</div>
									);
								})}
							</div>
						</div>
					</div>

					<div className="tier-journey-footer">
						{maxTier ? (
							<div className="tier-milestone-banner tier-milestone-max">
								🏆 Top tier unlocked — you&apos;re earning the max {currentTier.incentivePercent}% incentive!
							</div>
						) : (
							<div className="tier-milestone-banner">
								<span className="tier-milestone-amount">₹ {Math.round(amountToNext).toLocaleString('en-IN')}</span>
								{' '}more to unlock <strong>{nextTier.category}</strong> ({nextTier.incentivePercent}%)
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

const SelfInfoDashboard = ({ isEmbedded = false }) => {
	const history = useHistory();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const [dailyUpsellNetPaid, setDailyUpsellNetPaid] = useState(0);
	const [mtdNetPaid, setMtdNetPaid] = useState(0);

	const [todaysPgAmount, setTodaysPgAmount] = useState(0);
	const [todaysWalletAmount, setTodaysWalletAmount] = useState(0);
	const [mtdPgAmount, setMtdPgAmount] = useState(0);
	const [mtdWalletAmount, setMtdWalletAmount] = useState(0);

	useEffect(() => {
		const fetchDashboardData = async () => {
			try {
				setLoading(true);

				// Get current user's phlebo ID
				const userPhleboid = USER.user?.phleboid || USER.user?.phleboId;

				if (!userPhleboid) {
					throw new Error("User phlebo ID not found");
				}

				// Fetch current month data
				const today = new Date();
				const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

				const formatDate = (date) => {
					const year = date.getFullYear();
					const month = String(date.getMonth() + 1).padStart(2, '0');
					const day = String(date.getDate()).padStart(2, '0');
					return `${year}-${month}-${day}`;
				};

				// Fetch leaderboard data to get current user's performance
				const response = await fetch(
					`${URL.dev_url}upsell-self-info/${userPhleboid}`,
					{
						method: "GET",
						headers: {
							"Content-Type": "application/json",
							accessToken: USER.access_token,
						},
					},
				);

				if (!response.ok) {
					throw new Error("Failed to fetch user progress data");
				}

				const responseData = await response.json();
				const actualData = responseData.data || {};

				// Calculate totals
				const mtdPgAmount = actualData.monthTillDatePgAmount || 0;
				const mtdWalletAmount = actualData.monthTillDateWalletAmount || 0;
				const mtdTotal = mtdPgAmount + mtdWalletAmount;

				const todaysPgAmount = actualData.todaysPgAmount || 0;
				const todaysWalletAmount = actualData.todaysWalletAmount || 0;

				setTodaysPgAmount(todaysPgAmount);
				setTodaysWalletAmount(todaysWalletAmount);
				setDailyUpsellNetPaid(todaysPgAmount + todaysWalletAmount);

				// Set month-to-date data
				setMtdPgAmount(mtdPgAmount);
				setMtdWalletAmount(mtdWalletAmount);
				setMtdNetPaid(mtdTotal);

				setLoading(false);
			} catch (err) {
				setError(err.message || "An error occurred while fetching data");
				setLoading(false);
			}
		};

		fetchDashboardData();
	}, []);

	const handleBack = () => {
		history.goBack();
	};


	return (
		<div className="self-info-dashboard-container">
			{!isEmbedded && (
				<header className="dashboard-header">
					<nav className="navbar navbar-dark bg-primary">
						<button className="back-button" onClick={handleBack}>
							<i className="fa fa-arrow-left"></i>
						</button>
						<div className="navbar-brand">
							<h2 className="dashboard-title">My Progress Hub</h2>
						</div>
					</nav>
				</header>
			)}

			{loading && (
				<div className="loading-container">
					<div className="spinner"></div>
					<p>Loading dashboard...</p>
				</div>
			)}

			{!loading && error && (
				<div className="error-container">
					<p className="error-message">Error: {error}</p>
				</div>
			)}

			{!loading && !error && (
				<div className="dashboard-content">
					<div className="metrics-grid">
						{/* Daily Upsell Net Paid Card */}
						<div className="metric-card metric-card-today fade-in">
							<div className="card-header"><i className="fa fa-bolt"></i> Today&apos;s Net Upsell</div>
							<div className="card-body">
								<div className="metric-value">₹ {Math.round(dailyUpsellNetPaid).toLocaleString('en-IN')}</div>
								<div className="metric-label">Today</div>
								<div className="amount-breakdown">
									<div className="breakdown-item">
										<span className="breakdown-label"><span className="breakdown-dot pg-dot"></span>PG Amount</span>
										<span className="breakdown-value">₹ {Math.round(todaysPgAmount).toLocaleString('en-IN')}</span>
									</div>
									<div className="breakdown-item">
										<span className="breakdown-label"><span className="breakdown-dot wallet-dot"></span>Wallet Amount</span>
										<span className="breakdown-value">₹ {Math.round(todaysWalletAmount).toLocaleString('en-IN')}</span>
									</div>
								</div>
							</div>
						</div>

						{/* MTD Net Paid Card */}
						<div className="metric-card metric-card-month fade-in">
							<div className="card-header"><i className="fa fa-calendar"></i> Month&apos;s Net Upsell</div>
							<div className="card-body">
								<div className="metric-value">₹ {Math.round(mtdNetPaid).toLocaleString('en-IN')}</div>
								<div className="metric-label">Current Month</div>
								<div className="amount-breakdown">
									<div className="breakdown-item">
										<span className="breakdown-label"><span className="breakdown-dot pg-dot"></span>PG Amount</span>
										<span className="breakdown-value">₹ {Math.round(mtdPgAmount).toLocaleString('en-IN')}</span>
									</div>
									<div className="breakdown-item">
										<span className="breakdown-label"><span className="breakdown-dot wallet-dot"></span>Wallet Amount</span>
										<span className="breakdown-value">₹ {Math.round(mtdWalletAmount).toLocaleString('en-IN')}</span>
									</div>
								</div>
							</div>
						</div>



					</div>

					{/* Slab Progress Section */}
					<div className="slab-progress-section">
						<TierProgression title="PG Tier Progression" amount={mtdPgAmount} slabs={PHLEBO_PG_INCENTIVE_SLABS} />
						<TierProgression title="Wallet Tier Progression" amount={mtdWalletAmount} slabs={PHLEBO_WALLET_INCENTIVE_SLABS} />
					</div>
				</div>
			)}
		</div>
	);
};

export default SelfInfoDashboard;
